/**
 * Getting from "a page loaded" to "a session the SDK can act on".
 *
 * A hosted game never constructs its own credentials. The shell delivers a
 * `BOOTSTRAP` message once, on the iframe's first load, and everything the SDK
 * needs is in it. This module is the wait for that message, plus the sandbox
 * self-diagnostic the platform reads.
 *
 * The failure mode this is designed around is a developer running the game
 * directly — `vite dev`, or opening the file — and getting a hang. That is why
 * the wait has a deliberate timeout and a named error rather than a promise that
 * never settles: `NOT_LAUNCHED_BY_TRYPORTAL` tells you what actually happened,
 * where "connect() never resolved" would not.
 */
import { SDK_CHANNEL, parseShellMessage, } from './bridge-protocol.js';
import { attachBootstrapPort, EXTERNAL_BRIDGE_PROTOCOL, EXTERNAL_PORT_OFFER, EXTERNAL_PORT_ACK, } from './external-shell-bridge.js';
import { TRYPORTALError } from './errors.js';
/**
 * Resolve with the bootstrap the platform sends, or reject with a named error.
 *
 * The listener is attached before anything else happens in `connect()`, because
 * the shell posts `BOOTSTRAP` on the iframe's `load` event and a game that
 * subscribes late simply never sees it.
 */
export function waitForBootstrap(options = {}) {
    // `globalThis` is typed as having `addEventListener` in a DOM build, but this
    // package also runs under Node and in tests, so presence is checked rather
    // than assumed.
    const win = options.window ?? globalThis;
    const timeoutMs = options.timeoutMs ?? 10_000;
    if (typeof win.addEventListener !== 'function') {
        return Promise.reject(new TRYPORTALError('NOT_LAUNCHED_BY_TRYPORTAL', 'there is no window to receive a bootstrap on; this SDK runs in a browser'));
    }
    return new Promise((resolve, reject) => {
        let port;
        let offered = false;
        const listener = (event) => {
            // Only the shell may bootstrap this game.
            //
            // `event.origin` cannot be the check here: a sandboxed document without
            // `allow-same-origin` has an opaque origin, the shell must therefore post
            // with `'*'`, and `expectedParentOrigin` — the value that would be
            // compared against — arrives *inside* the message being validated. Window
            // identity is the property that does not depend on the message's own
            // claims.
            //
            // Without this, anything holding a reference to this frame could deliver a
            // BOOTSTRAP, and every later ACTION would be accepted against the session
            // it named. That is a path to text on the game's screen that never passed
            // the platform's moderation, which is the one thing 配信モード must not have.
            // The check is skipped only where there is no `parent` at all, which in
            // practice means Node. Every browser window has one — a top-level document's
            // `parent` is itself — so in a browser this is never bypassed, and an
            // `event.source` of `null` (a worker or a BroadcastChannel, neither of which
            // can be the shell) fails it like any other mismatch.
            if (win.parent !== undefined && event.source !== win.parent)
                return;
            const offer = event.data;
            if (offer?.['channel'] === SDK_CHANNEL &&
                offer['type'] === EXTERNAL_PORT_OFFER &&
                offer['protocol'] === EXTERNAL_BRIDGE_PROTOCOL) {
                if (offered ||
                    win.parent === undefined ||
                    event.source !== win.parent ||
                    typeof event.origin !== 'string' ||
                    !event.origin.startsWith('https://') ||
                    event.ports?.length !== 1)
                    return;
                const parentOrigin = event.origin;
                if (new URL(parentOrigin).origin !== parentOrigin)
                    return;
                offered = true;
                port = event.ports[0];
                if (port === undefined)
                    return;
                port.onmessage = (received) => {
                    const message = parseShellMessage(received.data);
                    if (message?.type !== 'BOOTSTRAP')
                        return;
                    const b = message.bootstrap;
                    if (b?.expectedParentOrigin !== parentOrigin ||
                        ![
                            'sessionToken',
                            'apiBaseUrl',
                            'realtimeUrl',
                            'sdkVersion',
                            'gameSessionId',
                            'rankingTrack',
                            'competitionId',
                        ].every((key) => typeof b[key] === 'string')) {
                        cleanup();
                        port?.close();
                        reject(new TRYPORTALError('NOT_LAUNCHED_BY_TRYPORTAL', 'external bootstrap origin or data is invalid'));
                        return;
                    }
                    if (port === undefined)
                        return;
                    port.onmessage = null;
                    attachBootstrapPort(b, port);
                    cleanup();
                    resolve(b);
                };
                port.start();
                port.postMessage({
                    channel: SDK_CHANNEL,
                    type: EXTERNAL_PORT_ACK,
                    protocol: EXTERNAL_BRIDGE_PROTOCOL,
                });
                return;
            }
            if (offered)
                return; // No window fallback after accepting a document port.
            const message = parseShellMessage(event.data);
            if (message?.type !== 'BOOTSTRAP')
                return;
            cleanup();
            resolve(message.bootstrap);
        };
        const timer = setTimeout(() => {
            cleanup();
            port?.close();
            reject(new TRYPORTALError('NOT_LAUNCHED_BY_TRYPORTAL', `no TRAIDENT bootstrap arrived within ${String(timeoutMs)}ms. ` +
                'A hosted game only receives one when the platform launches it; ' +
                'open the game through the TRAIDENT play page rather than directly.'));
        }, timeoutMs);
        // Deliberately not `unref`ed. This timer *is* the timeout, and an unrefed
        // timer does not fire in a quiet Node event loop — the process exits first
        // and the promise never settles. It is always cleared on the happy path.
        function cleanup() {
            clearTimeout(timer);
            win?.removeEventListener('message', listener);
        }
        win.addEventListener('message', listener);
    });
}
/**
 * Try, from inside the sandbox, everything the sandbox is supposed to stop.
 *
 * This is a self-diagnostic a publisher can read and corroborating evidence for
 * PRIV-001. It is not proof on its own — a game reporting "I could not reach
 * anything" is exactly as trustworthy as the game — which is why the browser
 * suite runs the same probes itself from outside the bundle.
 */
export async function runSandboxProbes() {
    let cookieAccess = 'BLOCKED';
    let cookieValue = '';
    try {
        const value = globalThis.document?.cookie;
        if (typeof value === 'string') {
            cookieValue = value;
            cookieAccess = value.length === 0 ? 'EMPTY' : 'LEAKED';
        }
    }
    catch {
        cookieAccess = 'BLOCKED';
    }
    let parentDom = 'BLOCKED';
    let parentDomError = '';
    try {
        const parent = globalThis.parent;
        const title = parent?.document?.title;
        parentDom = typeof title === 'string' ? 'READABLE' : 'BLOCKED';
    }
    catch (error) {
        parentDom = 'BLOCKED';
        parentDomError = error instanceof Error ? error.name : 'error';
    }
    /*
     * Passkeys, probed by actually asking for one.
     *
     * Checking that `navigator.credentials` exists is not the same question. The
     * object is present in a sandboxed document — it is *calling* it that gets
     * refused, three times over: the opaque origin, the runtime's
     * `publickey-credentials-get=()` Permissions-Policy, and the platform page
     * granting the feature to itself only. An existence check therefore reports
     * AVAILABLE for a capability that is comprehensively denied, which reads as a
     * containment failure and is the opposite of the truth.
     *
     * So the probe attempts the call and reports what the browser did with it.
     */
    let passkeyApi = 'BLOCKED';
    let passkeyError = '';
    try {
        const credentials = globalThis.navigator?.credentials;
        if (credentials == null || typeof credentials.get !== 'function') {
            passkeyApi = 'BLOCKED';
            passkeyError = 'credentials API absent';
        }
        else {
            await credentials.get({
                publicKey: {
                    challenge: new Uint8Array(32),
                    timeout: 1000,
                    allowCredentials: [],
                    userVerification: 'discouraged',
                },
            });
            // Reaching here means the browser was willing to run the ceremony, which
            // is the finding this probe exists to surface.
            passkeyApi = 'AVAILABLE';
        }
    }
    catch (error) {
        passkeyApi = 'BLOCKED';
        passkeyError = error instanceof Error ? error.name : 'error';
    }
    let storage = 'BLOCKED';
    try {
        const local = globalThis
            .localStorage;
        if (local != null) {
            local.getItem('tryportal.probe');
            storage = 'AVAILABLE';
        }
    }
    catch {
        storage = 'BLOCKED';
    }
    let origin = '';
    try {
        const value = globalThis.origin;
        origin = typeof value === 'string' ? value : '';
    }
    catch {
        origin = '';
    }
    return {
        cookieAccess,
        cookieValue,
        parentDom,
        parentDomError,
        passkeyApi,
        passkeyError,
        storage,
        origin,
    };
}
/** Post the probe results to the shell. Never throws. */
export async function reportSandboxProbes(post, targetOrigin) {
    try {
        post({ channel: SDK_CHANNEL, type: 'SANDBOX_PROBE', results: await runSandboxProbes() }, targetOrigin);
    }
    catch {
        // A diagnostic that breaks the game it is diagnosing is worse than no
        // diagnostic.
    }
}
